import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';
import ClauseBody from './ClauseBody';

// Create styles matching Laravel PDF
const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 11,
    lineHeight: 1.2,
  },

  // Cover Page Styles - Matching Screenshot Exactly
  coverPage: {
    position: 'relative',
    padding: 0,
    margin: 0,
  },
  coverBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover', // Ensure image covers entire page
    // zIndex: -100,
  },
  coverPageInnerView: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    width: '100%',
    position: 'relative',
  },
  coverFooterWebsite: {
    color: 'white',
    fontSize: 14,
    marginBottom: 5,
    textAlign: 'right',
    fontWeight: 'normal',
    alignItems: 'flex-end'
    // Note: textShadow is not supported in @react-pdf/renderer
    // Using semi-transparent background on parent (coverFooter) for visibility
  },
  coverFooterPhone: {
    color: 'white',
    fontSize: 22,
    textAlign: 'right',
    alignItems: 'flex-end',
    fontWeight: 'bold',
    // Note: textShadow is not supported in @react-pdf/renderer
    // Using semi-transparent background on parent (coverFooter) for visibility
  },
  coverLogo: {
    position: 'absolute',
    top: 35,
    right: 45,
    width: 180,
  },
  coverTitle: {
    position: 'absolute',
    top: 150,
    left: 65,
    fontSize: 32,
    fontWeight: 'bold',
    color: '#0F4C81', // Blue color matching screenshot
    lineHeight: 1.2,
    letterSpacing: -0.5,
    width: 280,
    backgroundColor: 'white',
    paddingRight: 50,
    paddingVertical: 10
  },
  coverAddress: {
    position: 'absolute',
    top: 300,
    left: 65,
    backgroundColor: '#0F4C81', // Blue box matching screenshot
    color: 'white',
    padding: 18,
    paddingHorizontal: 22,
    fontSize: 13,
    lineHeight: 1.2,
    width: 280,
  },
  coverAddressLine: {
    fontSize: 12,
    color: 'white',
    // marginBottom: 3,
  },
  coverFooter: {
    position: 'absolute',
    bottom: 50,
    right: 30,
    textAlign: 'right',
    // Semi-transparent background for better text visibility on image
    backgroundColor: 'rgba(0, 0, 0, 0.5)', // Dark semi-transparent background
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 4,
  },

  // Content Page Styles
  contentPage: {
    padding: 0,
  },
  pageHeader: {
    position: 'relative',
    height: 80,
    marginBottom: 20,
  },
  pageHeaderBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  pageHeaderLogo: {
    position: 'absolute',
    width: 180,
    left: 20,
    top: 12,
  },
  pageHeaderText: {
    fontSize: 24,
    color: 'white',
    fontWeight: 'bold',
  },
  pageContent: {
    padding: '0 1.2cm 1.2cm 1.2cm',
  },
  table: {
    display: 'table',
    width: '100%',
    marginBottom: 20,
    border: '1 solid #333',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #333',
  },
  tableHeader: {
    backgroundColor: '#1E3A8A',
    color: 'white',
    fontWeight: 'bold',
    padding: 8,
  },
  tableCell: {
    padding: 8,
    fontSize: 10,
    flex: 1,
    borderRight: '1 solid #333',
  },
  tableCellTitle: {
    width: '35%',
    backgroundColor: '#1E3A8A',
    color: 'white',
    fontWeight: 'bold',
    padding: 8,
    fontSize: 10,
  },
  tableCellContent: {
    width: '65%',
    padding: 8,
    fontSize: 10,
  },

  // Signature Styles
  signatureSection: {
    marginTop: 30,
  },
  signatureText: {
    fontSize: 11,
    marginBottom: 10,
  },
  signatureImage: {
    width: 150,
    height: 75,
    marginBottom: 10,
  },

  // Table of Contents Styles
  tocTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 20,
    color: '#1E3A8A',
  },
  tocTable: {
    display: 'table',
    width: '100%',
    border: '1 solid #333',
  },
  tocRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #333',
  },
  tocNumber: {
    width: '10%',
    padding: 8,
    fontSize: 10,
    borderRight: '1 solid #333',
  },
  tocItem: {
    width: '90%',
    padding: 8,
    fontSize: 10,
  },

  // Terms & Conditions Styles
  clauseTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1E3A8A',
    marginTop: 15,
    marginBottom: 8,
  },
  clauseText: {
    fontSize: 11,
    textAlign: 'justify',
    lineHeight: 1.3,
    marginBottom: 10,
  },
  bulletList: {
    marginLeft: 20,
    marginBottom: 10,
  },
  bulletItem: {
    fontSize: 11,
    marginBottom: 5,
    flexDirection: 'row',
  },
  bullet: {
    width: 15,
  },

  // Cost Schedule Table
  costTable: {
    display: 'table',
    width: '100%',
    border: '1 solid #333',
    marginTop: 20,
  },
  costTableHeader: {
    flexDirection: 'row',
    backgroundColor: '#1E3A8A',
    color: 'white',
    fontWeight: 'bold',
    borderBottom: '1 solid #333',
  },
  costTableRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #333',
  },
  costTableCellItem: {
    width: '25%',
    padding: 8,
    fontSize: 10,
    borderRight: '1 solid #333',
  },
  costTableCellWorks: {
    width: '50%',
    padding: 8,
    fontSize: 10,
    borderRight: '1 solid #333',
  },
  costTableCellCost: {
    width: '25%',
    padding: 8,
    fontSize: 10,
  },

  // Footer Page Styles
  footerPage: {
    padding: '1.2cm',
    textAlign: 'center',
  },
  footerCompanyName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1E3A8A',
    marginBottom: 10,
    textAlign: 'center',
  },
  footerAddress: {
    fontSize: 11,
    marginBottom: 5,
    textAlign: 'center',
  },
  footerContact: {
    fontSize: 11,
    marginTop: 15,
    marginBottom: 5,
    textAlign: 'center',
  },
  footerContactLabel: {
    color: '#1E3A8A',
    fontWeight: 'bold',
  },
  footerWebsite: {
    fontSize: 11,
    color: '#1E3A8A',
    fontWeight: 'bold',
    marginBottom: 5,
    textAlign: 'center',
  },
  footerLogos: {
    width: '70%',
    marginTop: 30,
    alignSelf: 'center',
  },
  footerLogosContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 30,
    width: '100%',
    gap: 8,
  },
  footerLogoItem: {
    width: 42,
    height: 28,
    objectFit: 'contain',
  },
});

const MTVHProposalPDF = ({ data, clauses }) => {
  if (!data) return null;

  // Clause bodies come from the database (property_proposal_clause_types /
  // property_proposal_clauses) with their {{placeholders}} already resolved against
  // this proposal by gg-be (propertyProposalClauseService).
  //
  // Note: some legacy MTVH clause rows name a payer in prose instead of using a
  // placeholder - the Utilities clause hardcoded "the Guardians pay", which contradicts
  // the Liabilities schedule whenever another payer is selected. gg-be rewrites those
  // back into their placeholder (normalizeClauseText) before substitution, so the
  // wording here does follow the liability options chosen on the form.
  //
  // The `fallback` text below is only rendered if a clause is missing from the database,
  // and must stay placeholder-driven for the same reason - never name a payer literally.
  const clauseHtml = clauses || {};
  const clauseStyles = {
    text: styles.clauseText,
    bulletItem: styles.bulletItem,
    bullet: styles.bullet,
  };

  // Map council_tax_liability ID to text value (matching Laravel config/property.php)
  const councilTaxLiabilityMap = {
    1: 'Global Guardians',
    2: 'Metropolitan',
  };

  const getCouncilTaxLiabilityText = (liabilityId) => {
    if (!liabilityId) return 'Global Guardians'; // Default fallback
    // Handle both string and number IDs
    const id = typeof liabilityId === 'string' ? parseInt(liabilityId) : liabilityId;
    return councilTaxLiabilityMap[id] || 'Global Guardians';
  };

  const proposedWorks = data.proposed_works || [];
  const unusualMatters = data.unusual_matters || '';

  return (
    <Document>
      {/* Page 1: Cover Page - Matching Screenshot Design */}
      <Page size="A4" style={styles.coverPage}>
        {/* Background Image - Building Photo */}
        <View style={styles.coverPageInnerView}>
          <Image
            src="/assets/images/reports/building_first_page.jpg"
            style={styles.coverBackground}
          />

          {/* Logo - Top Right Corner */}
          <Image
            src="/assets/images/GG_Logo_RGB_800.png"
            style={styles.coverLogo}
          />

          {/* Title - Left Side, Large Blue Text */}
          <Text style={styles.coverTitle}>
            PROPERTY{'\n'}PROTECTION{'\n'}PROPOSAL
          </Text>

          {/* Address Box - Blue Background, White Text */}
          <View style={styles.coverAddress}>
            <Text style={styles.coverAddressLine}>Metropolitan Thames Valley (MTVH)</Text>
            <Text style={styles.coverAddressLine}>{'\n'}</Text>
            <Text style={styles.coverAddressLine}>
              {`${data.property_address_1 || ''} ${data.property_address_2 || ''} ${data.property_address_3 || ''}`.trim() || 'The Grange 100 High Street Southgate,'}
            </Text>
            <Text style={styles.coverAddressLine}>
              {data.property_postcode || 'London N14 6PW'}
            </Text>
          </View>

          {/* Footer - Bottom Right, White Text on Image */}
          <View style={styles.coverFooter}>
            <Text style={styles.coverFooterWebsite}>www.global-guardians.co.uk</Text>
            <Text style={styles.coverFooterPhone}>0800 368 9601</Text>
          </View>
        </View>
      </Page>

      {/* Page 2: Property Details */}
      <Page size="A4" style={styles.contentPage}>
        {/* Header */}
        <View style={styles.pageHeader}>
          <Image
            src="/assets/images/reports/property_proposal_header_new.png"
            style={styles.pageHeaderBackground}
          />
          <Image
            src="/assets/images/GG_Logo_white.png"
            style={styles.pageHeaderLogo}
          />
        </View>

        <View style={styles.pageContent}>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <View style={styles.tableCellTitle}>
                <Text>Address:</Text>
              </View>
              <View style={styles.tableCellContent}>
                <Text>{`${data.property_address_1 || ''} ${data.property_address_2 || ''} ${data.property_address_3 || ''}`.trim() || 'N/A'}, {data.property_postcode || ''}</Text>
              </View>
            </View>

            <View style={styles.tableRow}>
              <View style={styles.tableCellTitle}>
                <Text>Start date with GG:</Text>
              </View>
              <View style={styles.tableCellContent}>
                <Text>{data.date_taken_on || new Date().toLocaleDateString('en-GB')}</Text>
              </View>
            </View>

            <View style={styles.tableRow}>
              <View style={styles.tableCellTitle}>
                <Text>Council Tax Liability:</Text>
              </View>
              <View style={styles.tableCellContent}>
                <Text>{getCouncilTaxLiabilityText(data.council_tax_liability)}</Text>
              </View>
            </View>

            <View style={styles.tableRow}>
              <View style={styles.tableCellTitle}>
                <Text>Proposed Works:</Text>
              </View>
              <View style={styles.tableCellContent}>
                {proposedWorks.length > 0 ? (
                  proposedWorks.map((work, index) => (
                    <View key={index} style={{ flexDirection: 'row', marginBottom: 3 }}>
                      <Text style={styles.bullet}>•</Text>
                      <Text style={{ flex: 1 }}>{work}</Text>
                    </View>
                  ))
                ) : (
                  <Text>No proposed works specified</Text>
                )}
              </View>
            </View>

            <View style={styles.tableRow}>
              <View style={styles.tableCellTitle}>
                <Text>Any unusual matters:</Text>
              </View>
              <View style={styles.tableCellContent}>
                <Text>{unusualMatters || 'None'}</Text>
              </View>
            </View>
          </View>
        </View>
      </Page>

      {/* Page 3: Signature Page */}
      <Page size="A4" style={styles.contentPage}>
        {/* Header */}
        <View style={styles.pageHeader}>
          <Image
            src="/assets/images/reports/property_proposal_header_new.png"
            style={styles.pageHeaderBackground}
          />
          <Image
            src="/assets/images/GG_Logo_white.png"
            style={styles.pageHeaderLogo}
          />
        </View>

        <View style={styles.pageContent}>
          <View style={styles.signatureSection}>
            <Text style={styles.signatureText}>
              Agreed by {data.client_contact_name || '____________________'} ………………………………………………………… (Metropolitan)
            </Text>

            <Text style={styles.signatureText}>Signature (GG):</Text>
            <Image
              src="/assets/images/nick_knight_signature.jpg"
              style={styles.signatureImage}
            />

            <Text style={styles.signatureText}>
              Date: ……………………………………………………………
            </Text>
          </View>
        </View>
      </Page>

      {/* Page 4: Table of Contents */}
      <Page size="A4" style={styles.contentPage}>
        {/* Header */}
        <View style={styles.pageHeader}>
          <Image
            src="/assets/images/reports/property_proposal_header_new.png"
            style={styles.pageHeaderBackground}
          />
          <Image
            src="/assets/images/GG_Logo_white.png"
            style={styles.pageHeaderLogo}
          />
        </View>

        <View style={styles.pageContent}>
          <Text style={styles.tocTitle}>Table of Contents</Text>

          <View style={styles.tocTable}>
            <View style={[styles.tocRow, { backgroundColor: '#1E3A8A' }]}>
              <View style={[styles.tocNumber, { color: 'white' }]}>
                <Text></Text>
              </View>
              <View style={[styles.tocItem, { color: 'white' }]}>
                <Text>Table of Contents</Text>
              </View>
            </View>

            {[
              'Single Point of Contact',
              'Guardians',
              'Start Date',
              'Minimum Contract Period',
              'Notice Period',
              'Fitting Out the Property',
              'Keys',
              'Monthly Report',
              'Maintenance Repairs',
              'Liability Insurance',
              'Council Tax',
              'Utilities',
              'Vacating the Property',
              'Costs & Works Schedule',
            ].map((item, index) => (
              <View key={index} style={styles.tocRow}>
                <View style={styles.tocNumber}>
                  <Text>{index + 1}.</Text>
                </View>
                <View style={styles.tocItem}>
                  <Text>{item}</Text>
                </View>
              </View>
            ))}
          </View>
        </View>
      </Page>

      {/* Page 5: Terms & Conditions (1-6) */}
      <Page size="A4" style={styles.contentPage}>
        {/* Header */}
        <View style={styles.pageHeader}>
          <Image
            src="/assets/images/reports/property_proposal_header_new.png"
            style={styles.pageHeaderBackground}
          />
          <Image
            src="/assets/images/GG_Logo_white.png"
            style={styles.pageHeaderLogo}
          />
        </View>

        <View style={styles.pageContent}>
          <Text style={styles.clauseTitle}>1. Single Point of Contact</Text>
          <ClauseBody
            html={clauseHtml.clause_single_point_of_contact}
            styles={clauseStyles}
            fallback={[
              "For all properties that this Owner places under GGMs management, GGM will provide the Owner with a single point of contact. Therefore all matters relating to the Property please shall be directed to the Owner's dedicated Global Guardians Account Manager, whose contact details are included on page 1 of the Proposal.",
            ]}
          />

          <Text style={styles.clauseTitle}>2. Guardians</Text>
          <ClauseBody
            html={clauseHtml.clause_guardians_metropolitan}
            styles={clauseStyles}
            fallback={[
              'GGM ensures the Guardians are strictly managed by assigning a Guardian Account Manager who manages the day to day activities of the Guardians, by appointing a resident Guardian on site (where possible) and by hosting regular property visits. GGM operates a 24 hour emergency service for all its properties and are able to remove or redistribute Guardians at times.',
              'The Property requires an adequate supply of Guardians for the safety of the Property, with the Guardian being an effective deterrent for an empty property. In addition to this, the Guardians ensure that any onsite Guardian presence will be distributed across the property. Due to the urgent necessity to secure this empty Property we will aim to get the first Guardian(s) on Property within 5 WORKING days.',
              'GGM may specifically aim to manage a property from being a live-in Guardian or may have to ask the relevant Guardians NOT to live-in the Property to ensure GGM can maintain reasonable use of the Property in emergency circumstances and for showing the property.',
            ]}
          />

          <Text style={styles.clauseTitle}>3. Start Date</Text>
          <ClauseBody
            html={clauseHtml.clause_start_date}
            styles={clauseStyles}
            fallback={[
              'The services provided by GGM will start on the date the Property is signed by the Owner ("the start date").',
            ]}
          />

          <Text style={styles.clauseTitle}>4. Minimum Contract Period</Text>
          <ClauseBody
            html={clauseHtml.clause_minimum_contract_period_metropolitan}
            styles={clauseStyles}
            fallback={[
              "The minimum contract period for GGM's services for the Property is a THREE (3) months.",
            ]}
          />

          <Text style={styles.clauseTitle}>5. Notice Period</Text>
          <ClauseBody
            html={clauseHtml.clause_notice_period_metropolitan}
            styles={clauseStyles}
            fallback={[
              'The Notice Period is from such date as GGM will give 32 days written notice to terminate agreement with the Owner in the event that GGM do not see further the use of the notice period may vary by agreement of both parties in advance upon the arrangement of licence agreements.',
            ]}
          />

          <Text style={styles.clauseTitle}>6. Fitting out the Property</Text>
          <ClauseBody
            html={clauseHtml.clause_fitting_out_the_property}
            styles={clauseStyles}
            fallback={[
              'In order to comply with Health & Safety legislation and Guardianship services are to be required to live and reside at the Property, GGM ensures that the Guardians are provided with domestic protection for the property.',
            ]}
          />
        </View>
      </Page>

      {/* Page 6: Terms & Conditions (7-12) */}
      <Page size="A4" style={styles.contentPage}>
        {/* Header */}
        <View style={styles.pageHeader}>
          <Image
            src="/assets/images/reports/property_proposal_header_new.png"
            style={styles.pageHeaderBackground}
          />
          <Image
            src="/assets/images/GG_Logo_white.png"
            style={styles.pageHeaderLogo}
          />
        </View>

        <View style={styles.pageContent}>
          <Text style={styles.clauseTitle}>7. Keys</Text>
          <ClauseBody
            html={clauseHtml.clause_keys_metropolitan}
            styles={clauseStyles}
            fallback={[
              'The Owner will be required to provide GGM with a full set of master keys to the Property.',
            ]}
          />

          <Text style={styles.clauseTitle}>8. Monthly Report</Text>
          <ClauseBody
            html={clauseHtml.clause_monthly_report_metropolitan}
            styles={clauseStyles}
            fallback={[
              'GGM will provide the Owner with a monthly report via email detailing the condition of the Property and advising of any maintenance issue in the property with images taken by GGM.',
            ]}
          />

          <Text style={styles.clauseTitle}>9. Maintenance Repairs</Text>
          <ClauseBody
            html={clauseHtml.clause_maintenance_repairs_metropolitan}
            styles={clauseStyles}
            fallback={[
              "GGM will attend to the reporting of maintenance and repair issues arising which require attention to preserve the Property's physical integrity and to avoid obvious health and safety problems arising in time and manage the following:",
              "GGM's in house maintenance team will be on call 24/7 to deal with any maintenance issues GGM and approved GGM contractors only carry out all works related to the internal parts of the property as listed in the Owner's responsibilities and as stated in the attached costing schedule.",
              'GGM will manage these emergency call outs should they arise such as plumbing leaks, gas leaks or emergency repairs including one system upkeep and external drainage systems will be the responsibility of the Owner.',
            ]}
          />

          <Text style={styles.clauseTitle}>10. Liability Insurance</Text>
          <ClauseBody
            html={clauseHtml.clause_liability_insurance_metropolitan}
            styles={clauseStyles}
            fallback={[
              'GGM has comprehensive Professional Indemnity insurance. A full copy of the contract of insurance can be provided on request.',
            ]}
          />

          {/* Clauses 11 and 12 carry the {{council_tax}} / {{utilities_*}} placeholders -
              they must follow the liability options selected on the proposal form. */}
          <Text style={styles.clauseTitle}>11. Council Tax</Text>
          <ClauseBody
            html={clauseHtml.clause_council_tax_metropolitan}
            styles={clauseStyles}
            fallback={[
              `${data.council_tax || 'GGM'} will be responsible for the payment of Council Tax for the Property.`,
            ]}
          />

          <Text style={styles.clauseTitle}>12. Utilities</Text>
          <ClauseBody
            html={clauseHtml.clause_utilities_metropolitan}
            styles={clauseStyles}
            fallback={[
              `${data.utilities_maintenance || 'GGM'} will be responsible for the maintenance of all utility outlets and ` +
              `${data.utilities_gas_electric || 'GGM'} will be responsible for the payment of the utility bills levied for the Property. ` +
              `Prior to the Guardians taking residence, the Owner will confirm that the utilities are in working order, ` +
              `including specifically the electrical, lighting and heating systems.`,
            ]}
          />
        </View>
      </Page>

      {/* Page 7: Terms & Conditions (13) */}
      <Page size="A4" style={styles.contentPage}>
        {/* Header */}
        <View style={styles.pageHeader}>
          <Image
            src="/assets/images/reports/property_proposal_header_new.png"
            style={styles.pageHeaderBackground}
          />
          <Image
            src="/assets/images/GG_Logo_white.png"
            style={styles.pageHeaderLogo}
          />
        </View>

        <View style={styles.pageContent}>
          <Text style={styles.clauseTitle}>13. Vacating the Property</Text>
          <ClauseBody
            html={clauseHtml.clause_vacating_the_property_metropolitan}
            styles={clauseStyles}
            fallback={[
              "Upon termination of this agreement between GGM and the Owner, the Guardians will vacate the Property. GGM will return all keys for the Property to the Owner. A Property Manager form will be signed and dated by both GGM's client satisfaction team and the Owner, to be provided upon return of the Property.",
            ]}
          />
        </View>
      </Page>

      {/* Page 8: Cost & Works Schedule */}
      <Page size="A4" style={styles.contentPage}>
        {/* Header */}
        <View style={styles.pageHeader}>
          <Image
            src="/assets/images/reports/property_proposal_header_new.png"
            style={styles.pageHeaderBackground}
          />
          <Image
            src="/assets/images/GG_Logo_white.png"
            style={styles.pageHeaderLogo}
          />
        </View>

        <View style={styles.pageContent}>
          <Text style={styles.clauseTitle}>14. The Cost and Works Schedule for the Property</Text>

          <View style={styles.costTable}>
            <View style={styles.costTableHeader}>
              <Text style={styles.costTableCellItem}>ITEM</Text>
              <Text style={styles.costTableCellWorks}>WORKS TO BE DONE</Text>
              <Text style={styles.costTableCellCost}>COST (£)</Text>
            </View>

            <View style={styles.costTableRow}>
              <Text style={styles.costTableCellItem}>Intake Fee</Text>
              <View style={styles.costTableCellWorks}>
                <Text>• Administration of the Property.</Text>
              </View>
              <Text style={styles.costTableCellCost}>£ FREE OF CHARGE</Text>
            </View>

            <View style={styles.costTableRow}>
              <Text style={styles.costTableCellItem}>Weekly Waste Collection Fee</Text>
              <View style={styles.costTableCellWorks}>
                <Text>• Removal of waste from Property.</Text>
              </View>
              <Text style={styles.costTableCellCost}>£ FREE OF CHARGE</Text>
            </View>

            <View style={styles.costTableRow}>
              <Text style={styles.costTableCellItem}>Weekly Management Fee</Text>
              <View style={styles.costTableCellWorks}>
                <Text>• Provision of Guardian Management services to the Property.</Text>
                <Text>• Monthly inspection report.</Text>
                <Text>• Free of charge listing on Emergency Help Desk System.</Text>
                <Text>• Single point of contact for the Owner's requirements.</Text>
              </View>
              <Text style={styles.costTableCellCost}>£ FREE OF CHARGE</Text>
            </View>

            <View style={styles.costTableRow}>
              <Text style={styles.costTableCellItem}>Keys</Text>
              <View style={styles.costTableCellWorks}>
                <Text>• Owner to provide one complete set of master keys.</Text>
              </View>
              <Text style={styles.costTableCellCost}>£ FREE OF CHARGE</Text>
            </View>

            <View style={styles.costTableRow}>
              <Text style={styles.costTableCellItem}>Electrics</Text>
              <View style={styles.costTableCellWorks}>
                <Text>• Electrical certification to be provided by GGM.</Text>
              </View>
              <Text style={styles.costTableCellCost}>£ FREE OF CHARGE</Text>
            </View>

            <View style={styles.costTableRow}>
              <Text style={styles.costTableCellItem}>Gas</Text>
              <View style={styles.costTableCellWorks}>
                <Text>• Gas Safe certification to be provided by GGM.</Text>
              </View>
              <Text style={styles.costTableCellCost}>£ FREE OF CHARGE</Text>
            </View>

            <View style={styles.costTableRow}>
              <Text style={styles.costTableCellItem}>Fire Safety Works</Text>
              <View style={styles.costTableCellWorks}>
                <Text>• GGM to provide a fire blanket in the kitchen area and carbon monoxide detector in proximity to the boiler.</Text>
              </View>
              <Text style={styles.costTableCellCost}>£ FREE OF CHARGE</Text>
            </View>

            <View style={styles.costTableRow}>
              <Text style={styles.costTableCellItem}>Works Required</Text>
              <View style={styles.costTableCellWorks}>
                <Text>• GGM will carry out all works required to bring the properties up to habitable standard.</Text>
              </View>
              <Text style={styles.costTableCellCost}>£ FREE OF CHARGE</Text>
            </View>

            <View style={styles.costTableRow}>
              <Text style={[styles.costTableCellItem, { fontWeight: 'bold' }]}>TOTAL COSTS</Text>
              <View style={styles.costTableCellWorks}>
                <Text style={{ fontWeight: 'bold' }}>• Fit out works will be covered by GGM so there will be no cost to the Owner.</Text>
              </View>
              <Text style={[styles.costTableCellCost, { fontWeight: 'bold' }]}>£ FREE OF CHARGE</Text>
            </View>
          </View>
        </View>
      </Page>

      {/* Page 9: Footer/Contact Page */}
      <Page size="A4" style={styles.contentPage}>
        {/* Header */}
        <View style={styles.pageHeader}>
          <Image
            src="/assets/images/reports/property_proposal_header_new.png"
            style={styles.pageHeaderBackground}
          />
          <Image
            src="/assets/images/GG_Logo_white.png"
            style={styles.pageHeaderLogo}
          />
        </View>

        <View style={styles.pageContent}>
          <View style={{ marginTop: 200, textAlign: 'center' }}>
            <Text style={styles.footerCompanyName}>Global Guardians</Text>
            <Text style={styles.footerAddress}>Sopers House</Text>
            <Text style={styles.footerAddress}>Sopers Road</Text>
            <Text style={styles.footerAddress}>Cuffley</Text>
            <Text style={styles.footerAddress}>Potters Bar</Text>
            <Text style={styles.footerAddress}>EN6 4RY</Text>
            <Text style={styles.footerAddress}>UK</Text>

            <Text style={[styles.footerContact, { marginTop: 30 }]}>
              <Text style={styles.footerContactLabel}>t:</Text> 0800 368 9601
            </Text>
            <Text style={styles.footerContact}>
              <Text style={styles.footerContactLabel}>e:</Text> enquiries@global-guardians.co.uk
            </Text>
            <Text style={styles.footerWebsite}>
              www.Global-Guardians.co.uk
            </Text>

            <View style={styles.footerLogosContainer}>
              <Image
                src="/assets/images/mtvh-pp-logos-pdf/mtvh-pp-logo-pdf-1.png"
                style={styles.footerLogoItem}
              />
              <Image
                src="/assets/images/mtvh-pp-logos-pdf/mtvh-pp-logo-pdf-2.png"
                style={styles.footerLogoItem}
              />
              <Image
                src="/assets/images/mtvh-pp-logos-pdf/mtvh-pp-logo-pdf-3.png"
                style={styles.footerLogoItem}
              />
              <Image
                src="/assets/images/mtvh-pp-logos-pdf/mtvh-pp-logo-pdf-4.png"
                style={styles.footerLogoItem}
              />
              <Image
                src="/assets/images/mtvh-pp-logos-pdf/mtvh-pp-logo-pdf-5.png"
                style={styles.footerLogoItem}
              />
              <Image
                src="/assets/images/mtvh-pp-logos-pdf/mtvh-pp-logo-pdf-6.png"
                style={styles.footerLogoItem}
              />
              <Image
                src="/assets/images/mtvh-pp-logos-pdf/mtvh-pp-logo-pdf-7.png"
                style={styles.footerLogoItem}
              />
              <Image
                src="/assets/images/mtvh-pp-logos-pdf/mtvh-pp-logo-pdf-8.png"
                style={styles.footerLogoItem}
              />
              <Image
                src="/assets/images/mtvh-pp-logos-pdf/mtvh-pp-logo-pdf-9.png"
                style={styles.footerLogoItem}
              />
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default MTVHProposalPDF;
